/**
 * Minimal Server-Sent Events parser for streaming fetch responses.
 *
 * Our chat API emits JSON-encoded events:
 *   data: {"type":"delta","text":"hello"}\n\n
 *   data: {"type":"done","meta":{...}}\n\n
 *
 * Usage:
 *   for await (const evt of readSseStream<ChatStreamEvent>(resp.body)) { ... }
 */
export async function* readSseStream<T>(
  body: ReadableStream<Uint8Array> | null,
): AsyncGenerator<T, void, unknown> {
  if (!body) return;

  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line
      let sepIndex: number;
      while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, sepIndex);
        buffer = buffer.slice(sepIndex + 2);

        const data = parseSseEvent(rawEvent);
        if (data === null) continue;

        try {
          yield JSON.parse(data) as T;
        } catch {
          // Skip malformed frames — the server is the only producer,
          // but if one event is bad we don't want to abort the whole stream.
        }
      }
    }

    // Flush any trailing event (no blank line terminator)
    const trailing = parseSseEvent(buffer);
    if (trailing) {
      try {
        yield JSON.parse(trailing) as T;
      } catch {
        /* ignore */
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseEvent(raw: string): string | null {
  // Join multi-line `data:` values with '\n' per the SSE spec.
  const lines = raw.split('\n');
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }
  if (dataLines.length === 0) return null;
  return dataLines.join('\n');
}
